import {
  MANUAL_CONNECT_CRMS,
  MANUAL_CONNECT_CRM_IDS,
  ManualConnectCrmId,
  ManualCrmField,
  ROLLOUT_CRMS,
} from '../constants/rolloutCrms';
import {
  CRM_OPTIONS,
  isValidWebUrl,
} from '../components/features/onboarding/schemas/onboarding.schema';
import { hasAnyConnectedCrm, type RolloutCheckSources } from './rollout';
import { toHttpsUrl } from './urlUtils';

export type ManualCrmValues = Partial<Record<ManualCrmField, string>>;

export const MANUAL_CRM_FIELD_LABELS: Record<ManualCrmField, string> = {
  site_url: 'Site address',
  api_key: 'API key',
  username: 'Username',
  password: 'Password',
};

/** The field's label for this CRM, e.g. "API Token" for BoldTrail. */
export const manualCrmFieldLabel = (crm: ManualConnectCrmId, field: ManualCrmField): string =>
  (MANUAL_CONNECT_CRMS[crm] as { labels?: Partial<Record<ManualCrmField, string>> }).labels?.[
    field
  ] ?? MANUAL_CRM_FIELD_LABELS[field];

/** Field errors for a manual CRM connection; empty when every required field is valid. */
export const validateManualCrmValues = (
  crm: ManualConnectCrmId,
  values: ManualCrmValues
): Partial<Record<ManualCrmField, string>> => {
  const errors: Partial<Record<ManualCrmField, string>> = {};
  for (const field of MANUAL_CONNECT_CRMS[crm].fields) {
    const value = values[field]?.trim() ?? '';
    if (!value) errors[field] = `${manualCrmFieldLabel(crm, field)} is required`;
    else if (field === 'site_url' && !isValidWebUrl(value)) {
      errors[field] = 'Please enter a valid site address (e.g., yoursite.com)';
    }
  }
  return errors;
};

/** The submitted values for the CRM's fields only, trimmed; the site address as https://. */
export const cleanManualCrmValues = (
  crm: ManualConnectCrmId,
  values: ManualCrmValues
): ManualCrmValues =>
  Object.fromEntries(
    MANUAL_CONNECT_CRMS[crm].fields.map((field) => {
      const value = values[field]?.trim() ?? '';
      return [field, field === 'site_url' ? toHttpsUrl(value) : value];
    })
  );

/** A client's request for the team to connect a CRM, kept on onboardingData. */
export interface CrmConnectRequest {
  status: 'submitted';
  submitted_at: string;
}

type OnboardingDataWithRequests = {
  crm_connect_requests?: Partial<Record<string, CrmConnectRequest>>;
  [key: string]: unknown;
};

export const getCrmConnectRequest = (
  onboardingData: unknown,
  crm: ManualConnectCrmId
): CrmConnectRequest | undefined =>
  (onboardingData as OnboardingDataWithRequests | undefined)?.crm_connect_requests?.[crm];

/** onboardingData with this CRM's connect request recorded (other CRMs' requests kept). */
export const withCrmConnectRequest = (
  onboardingData: unknown,
  crm: ManualConnectCrmId,
  submittedAt: string
): OnboardingDataWithRequests => {
  const data = (onboardingData ?? {}) as OnboardingDataWithRequests;
  return {
    ...data,
    crm_connect_requests: {
      ...data.crm_connect_requests,
      [crm]: { status: 'submitted', submitted_at: submittedAt },
    },
  };
};

interface IntegrationsOrg {
  onboardingSetup?: { stage5?: { completed?: boolean } } | null;
  onboardingData?: unknown;
  sync_link?: string | null;
  crmConnections?: RolloutCheckSources['crmConnections'];
  rollout?: RolloutCheckSources['rollout'];
}

const crmLabel = (entry: { crm_type?: string; crm_other_name?: string }): string =>
  entry.crm_type === 'other'
    ? entry.crm_other_name?.trim() || 'CRM'
    : (CRM_OPTIONS.find((option) => option.value === entry.crm_type)?.label ??
      entry.crm_type ??
      'CRM');

/**
 * CRMs whose setup is still with the team, for the "Integration Setup in
 * Progress" banner: Connect-form submissions, plus the CRMs chosen in
 * onboarding (none when Follow Up Boss was chosen; the client connects it).
 * Empty once any CRM is connected in Rollout or the sync link is set.
 */
export const getIntegrationsInProgress = (org: IntegrationsOrg | null | undefined): string[] => {
  if (!org) return [];
  const sources = { crmConnections: org.crmConnections, rollout: org.rollout };
  if (hasAnyConnectedCrm(sources) || org.sync_link?.trim()) return [];

  const submitted = MANUAL_CONNECT_CRM_IDS.filter((crm) =>
    getCrmConnectRequest(org.onboardingData, crm)
  ).map((crm) => ROLLOUT_CRMS[crm].label);

  const entries =
    (
      org.onboardingData as {
        crm?: { crm_entries?: { crm_type?: string; crm_other_name?: string }[] };
      }
    )?.crm?.crm_entries ?? [];
  const fromOnboarding =
    org.onboardingSetup?.stage5?.completed && !entries.some((e) => e.crm_type === 'fub')
      ? entries.filter((e) => e.crm_type).map(crmLabel)
      : [];

  return [...new Set([...submitted, ...fromOnboarding])];
};
